'use client';

import {
  useBankAccount,
  useBankAccountFieldsConfig,
} from '@commenda/api/bankAccount';
import { useBusinessEntity } from '@commenda/api/business-entity';
import { createCommendaOSEntity } from '@commenda/api/commenda-os-dsl';
import { useApiScope } from '@commenda/api/scope';
import {
  Button,
  Icons,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { CountryCodeMap } from '@commenda/kakashi/src/components/CountryInput/countryCodeMap';
import type { DataSourceConfig } from '@commenda/types';
import type {
  DSLContext,
  FieldValue,
} from '@commenda/types/src/commenda-os-dsl';
import { Country } from '@prisma/client';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BankAccountFields } from '../../../bankAccount/BankAccountFields';
import { IndirectTaxRegistrationHelper } from '../../../indirect-tax/registrations/utils/IndirectTaxRegistrationHelper';
import {
  getDefaultCurrency,
  validateBankAccountFields,
} from '../../config/country-bank-utils';
import { DSLContextMarker } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { strOrUndef } from '../../utils/context-values';
import { isRuleActive } from '../../utils/validation';
import { DEFAULT_JURISDICTION_CONTEXT_KEY } from './IndirectTaxRegistration/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

const ADD_NEW_VALUE = '__add_new__';

/** Value stored in formData[fieldId] */
export interface BankAccountFieldValue {
  selectedId: string | null;
  displayLabel?: string | null;
  country?: string | null;
}

interface BankAccountProps {
  fieldId: string;
  label?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  dataSource?: DataSourceConfig;
  props?: {
    mode?: 'read' | 'read-write';
    defaultCountry?: string;
    disabled?: boolean;
    addNewLabel?: string;
    /** Controls the country field behaviour: 'any' = freely selectable, 'locked' = entity's incorporation country, 'locked-default' = configured default country */
    bankCountryScope?: { mode: 'any' | 'locked' | 'locked-default' };
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export const BankAccountComponent = ({
  fieldId,
  label: labelOverride,
  guidance,
  description: descriptionOverride,
  validation,
  dataSource,
  props = {},
}: BankAccountProps) => {
  const { shareToken } = useApiScope();

  const {
    formData,
    updateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
    errors,
    setErrors,
    isPreview,
    registerPreviewFillHandler,
  } = useContext(FormContext);

  const {
    mode = 'read-write',
    defaultCountry = null,
    disabled: propsDisabled = false,
    addNewLabel = 'Add new bank account',
    bankCountryScope,
  } = props;

  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
  const VALID_CONTEXT_KEYS = ['companyId', 'corporationId', 'directorId'];

  // ── Effective DSL context ──────────────────────────────────────────────────
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };

    if (contextBindings) {
      for (const binding of contextBindings) {
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) continue;
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) {
          console.warn(
            `[BankAccount] Skipping invalid context key: ${binding.contextKey}`
          );
          continue;
        }
        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }

    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) {
        filteredCtx[key] = ctx[key];
      }
    }
    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  // ── Missing context detection ──────────────────────────────────────────────
  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings || contextBindings.length === 0) return false;
    const validBindings = contextBindings.filter((b) =>
      VALID_CONTEXT_KEYS.includes(b.contextKey)
    );
    if (validBindings.length === 0) return false;
    return validBindings.some((binding) => {
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return (
          dslContext?.[binding.contextKey] === undefined ||
          dslContext?.[binding.contextKey] === null
        );
      }
      const v = formData[binding.sourceFieldId];
      return v === undefined || v === null || v === '';
    });
  }, [contextBindings, formData, dslContext]);

  // ── Read all bank accounts for the corporation ─────────────────────────────
  // useBankAccount only needs corporationId — avoids the companyId requirement
  // of useCommendaOSRead so the form builder preview works in all contexts.
  const { data: bankAccountsData, mutate } = useBankAccount({
    corporationId: effectiveContext?.corporationId ?? 0,
  });
  const isLoading = effectiveContext?.corporationId
    ? bankAccountsData === undefined
    : false;
  const bankAccounts =
    effectiveContext?.corporationId && !hasMissingBoundContext
      ? (bankAccountsData?.accounts ?? null)
      : null;

  // ── Read corporation to get incorporation country ──────────────────────────
  const { data: businessEntity } = useBusinessEntity(
    effectiveContext?.corporationId ?? 0
  );
  const entityCountry =
    businessEntity?.generalCorporationInfo.incorporationCountry ?? null;

  const jurisdictionCountry = useMemo(() => {
    const country = IndirectTaxRegistrationHelper.getCountryFromJurisdictionId(
      strOrUndef(dslContext?.[DEFAULT_JURISDICTION_CONTEXT_KEY]) ?? ''
    );
    return country in CountryCodeMap ? country : null;
  }, [dslContext]);

  // Country to use when the inline add form opens, based on scope mode
  const effectiveStartCountry = useMemo(() => {
    if (bankCountryScope?.mode === 'locked') return entityCountry ?? '';
    if (bankCountryScope?.mode === 'locked-default')
      return defaultCountry ?? '';
    // Every BankAccount is created with defaultCountry "US", so it can't tell us what the author wanted.
    return jurisdictionCountry ?? defaultCountry ?? entityCountry ?? '';
  }, [
    bankCountryScope?.mode,
    entityCountry,
    defaultCountry,
    jurisdictionCountry,
  ]);

  // ── Stored data & local form state ─────────────────────────────────────────
  const storedData = formData[fieldId] as BankAccountFieldValue | undefined;
  const selectedId = storedData?.selectedId ?? null;

  const [formMode, setFormMode] = useState<'idle' | 'add'>('idle');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // In preview mode, bank accounts created inline are kept here so they appear
  // in the select list without ever touching the backend.
  const [previewBankAccounts, setPreviewBankAccounts] = useState<
    { value: string; label: string }[]
  >([]);

  // ── Select options ─────────────────────────────────────────────────────────
  const selectOptions = useMemo(() => {
    const apiOptions = Array.isArray(bankAccounts)
      ? bankAccounts
          .filter((a) => a.id)
          .map((a) => {
            const accountNumber =
              a.credentials?.accountNumber ?? a.accountNumberMask;
            const mask = accountNumber
              ? ` — ••••${accountNumber.slice(-4)}`
              : '';
            const label = `${a.bankName || 'Bank Account'}${mask}`;
            return { value: a.id as string, label };
          })
      : [];
    // In preview mode, merge locally-created accounts so they appear in the list.
    return [...apiOptions, ...previewBankAccounts];
  }, [bankAccounts, previewBankAccounts]);

  // Maps accountId → country for mismatch validation on selection
  const accountCountryMap = useMemo(() => {
    if (!Array.isArray(bankAccounts)) return {} as Record<string, string>;
    return Object.fromEntries(
      bankAccounts
        .filter((a) => a.id && a.country)
        .map((a) => [a.id, a.country])
    ) as Record<string, string>;
  }, [bankAccounts]);

  // ── New form state (feature-flagged) ──────────────────────────────────────
  const [newBase, setNewBase] = useState({
    accountHolderName: '',
    country: '',
    bankName: '',
    currency: '',
  });
  const [newCredentials, setNewCredentials] = useState<Record<string, string>>(
    {}
  );
  const [newErrors, setNewErrors] = useState<Record<string, string>>({});
  const { config } = useBankAccountFieldsConfig();

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === ADD_NEW_VALUE) {
        setNewBase({
          accountHolderName:
            businessEntity?.generalCorporationInfo.legalName ?? '',
          country: effectiveStartCountry,
          bankName: '',
          currency: getDefaultCurrency(effectiveStartCountry),
        });
        setNewCredentials({});
        setNewErrors({});
        setFormMode('add');
        setSaveError(null);
        // Clear any country mismatch error when switching to add-new
        const { [`${fieldId}__countryMismatch`]: _removed, ...rest } = errors;
        setErrors(rest);
        return;
      }
      const selectedOption = selectOptions.find((opt) => opt.value === value);
      updateField(fieldId, {
        selectedId: value || null,
        displayLabel: selectedOption?.label ?? null,
        country: accountCountryMap[value] ?? null,
      } satisfies BankAccountFieldValue);
      setFormMode('idle');
      setSaveError(null);

      // Country mismatch validation
      const isRestricted =
        bankCountryScope?.mode === 'locked' ||
        bankCountryScope?.mode === 'locked-default';
      if (isRestricted && effectiveStartCountry && value) {
        const selectedAccountCountry = accountCountryMap[value];
        if (
          selectedAccountCountry &&
          selectedAccountCountry !== effectiveStartCountry
        ) {
          const countryName =
            CountryCodeMap[effectiveStartCountry as Country] ??
            effectiveStartCountry;
          setErrors({
            ...errors,
            [`${fieldId}__countryMismatch`]: `Please select a bank account for ${countryName}`,
          });
        } else {
          const { [`${fieldId}__countryMismatch`]: _removed, ...rest } = errors;
          setErrors(rest);
        }
      } else {
        const { [`${fieldId}__countryMismatch`]: _removed, ...rest } = errors;
        setErrors(rest);
      }
    },
    [
      fieldId,
      updateField,
      effectiveStartCountry,
      defaultCountry,
      selectOptions,
      businessEntity?.generalCorporationInfo.legalName,
      bankCountryScope?.mode,
      accountCountryMap,
      errors,
      setErrors,
    ]
  );

  // Preview self-fill: select the first loaded bank account — a real record
  // only this component can see, so the preview Fill button delegates to us.
  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler || !fieldId) return undefined;
    registerPreviewFillHandler(fieldId, () => {
      if (storedData?.selectedId) return { kind: 'already-filled' };
      if (isLoading) return { kind: 'loading' };
      const firstAccountOption = selectOptions.find(
        (option) => option.value !== ADD_NEW_VALUE
      );
      if (!firstAccountOption) {
        return {
          kind: 'skipped',
          reason: 'no bank accounts on this corporation — add one manually',
        };
      }
      handleSelectChange(firstAccountOption.value);
      return { kind: 'filled' };
    });
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    storedData?.selectedId,
    isLoading,
    selectOptions,
    handleSelectChange,
  ]);

  const handleCancel = useCallback(() => {
    setFormMode('idle');
    setNewBase({
      accountHolderName: businessEntity?.generalCorporationInfo.legalName ?? '',
      country: effectiveStartCountry,
      bankName: '',
      currency: getDefaultCurrency(effectiveStartCountry),
    });
    setNewCredentials({});
    setNewErrors({});
    setSaveError(null);
  }, [
    effectiveStartCountry,
    defaultCountry,
    businessEntity?.generalCorporationInfo.legalName,
  ]);

  // New form save
  const handleNewFormSave = useCallback(async () => {
    if (!effectiveContext?.corporationId || !config) return;
    const validationErrors = validateBankAccountFields(
      config,
      newBase,
      newCredentials
    );
    if (Object.keys(validationErrors).length > 0) {
      setNewErrors(validationErrors);
      return;
    }

    // In preview mode, simulate a successful save without hitting the backend.
    // The created account is kept in local state so it appears in the select list.
    if (isPreview) {
      const previewId = `preview-${Date.now()}`;
      const label = newBase.bankName || 'Bank Account';
      setPreviewBankAccounts((prev) => [...prev, { value: previewId, label }]);
      updateField(fieldId, {
        selectedId: previewId,
        displayLabel: label,
        country: newBase.country || null,
      } satisfies BankAccountFieldValue);
      setFormMode('idle');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const dtoCredentials: Record<string, string> = {};
      Object.entries(newCredentials).forEach(([key, val]) => {
        const v = val?.trim();
        if (v) dtoCredentials[key] = v;
      });
      const fields: FieldValue[] = [
        { path: 'accountHolderName', value: newBase.accountHolderName.trim() },
        { path: 'bankName', value: newBase.bankName.trim() },
        { path: 'country', value: newBase.country },
        { path: 'currency', value: newBase.currency },
      ];
      if (Object.keys(dtoCredentials).length > 0) {
        fields.push({ path: 'credentials', value: dtoCredentials });
      }
      const result = await createCommendaOSEntity(
        'bankAccount',
        fields,
        effectiveContext as DSLContext,
        shareToken
      );
      if (!result.result.success || !result.result.id) {
        throw new Error(result.result.error || 'Failed to create bank account');
      }
      updateField(fieldId, {
        selectedId: String(result.result.id),
        displayLabel: newBase.bankName || 'Bank Account',
        country: newBase.country || null,
      } satisfies BankAccountFieldValue);
      await mutate();
      setFormMode('idle');
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save bank account'
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    newBase,
    newCredentials,
    effectiveContext,
    fieldId,
    updateField,
    mutate,
    config,
    isPreview,
    setPreviewBankAccounts,
  ]);

  // ── Display state ──────────────────────────────────────────────────────────
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);
  const isReadOnly = mode === 'read';
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    hasMissingBoundContext;

  const effectiveLabel = labelOverride || 'Bank Account';
  const effectiveDescription = descriptionOverride;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">
            Loading bank accounts...
          </span>
        </div>
      </div>
    );
  }

  // ── Missing context states ─────────────────────────────────────────────────
  if (hasMissingBoundContext) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            Select a corporation above first...
          </p>
        </div>
      </div>
    );
  }

  if (!effectiveContext?.corporationId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to load bank accounts.
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="mb-4" data-field-id={fieldId}>
      <LabelWithGuidanceTooltip
        label={effectiveLabel}
        guidance={guidance?.text}
        isRequired={isRequired}
        description={effectiveDescription}
      />

      <div className="rounded-lg border border-border-secondary bg-bg-primary overflow-hidden">
        <div className="p-4 space-y-3">
          {/* ── Select existing bank account ─────────────────────────── */}
          <Select
            value={selectedId ?? ''}
            onValueChange={handleSelectChange}
            disabled={isDisabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a bank account" />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.length === 0 ? (
                <div className="px-2 py-2 text-sm text-text-tertiary text-center">
                  No bank accounts yet
                </div>
              ) : (
                selectOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))
              )}
              {!isReadOnly && !isDisabled && (
                <>
                  {selectOptions.length > 0 && (
                    <div className="my-1 h-px bg-border-secondary" />
                  )}
                  <SelectItem value={ADD_NEW_VALUE}>
                    <span className="flex items-center gap-1.5 text-text-interactive">
                      <Icons.Plus className="w-3.5 h-3.5" />
                      {addNewLabel}
                    </span>
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>

          {/* ── Country mismatch error ──────────────────────────────── */}
          {errors[`${fieldId}__countryMismatch`] && formMode === 'idle' && (
            <div className="flex items-center gap-1.5 text-sm text-text-error">
              <Icons.AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errors[`${fieldId}__countryMismatch`]}</span>
            </div>
          )}

          {/* ── Inline add form ──────────────────────────────────────── */}
          {formMode === 'add' && (
            <div className="space-y-3 pt-3 border-t border-border-secondary">
              <BankAccountFields
                accountHolderName={newBase.accountHolderName}
                country={newBase.country}
                bankName={newBase.bankName}
                currency={newBase.currency}
                credentials={newCredentials}
                errors={newErrors}
                onBaseFieldChange={(field, value) => {
                  setNewBase((prev) => ({ ...prev, [field]: value }));
                  if (field === 'country') setNewCredentials({});
                }}
                onCredentialChange={(key, val) =>
                  setNewCredentials((prev) => ({ ...prev, [key]: val }))
                }
                onErrorChange={setNewErrors}
                bankCountryScope={bankCountryScope}
              />
              {saveError && (
                <div className="flex items-center gap-1.5 text-sm text-text-error">
                  <Icons.AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{saveError}</span>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleNewFormSave}
                  disabled={isSaving || !config}
                >
                  {isSaving ? (
                    <span className="flex items-center gap-1.5">
                      <LoadingIndicator size={16} />
                      Saving...
                    </span>
                  ) : (
                    'Save'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
